import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  DialOption as DialOptionType,
  RouteType,
} from '@weave/schema-gen-ts/dist/schemas/phone/tree/v1/phone_tree_service.pb';
import { EditableText } from '@frontend/editable-text';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useSettingsNavigate, useSettingsMatch, useNavigationBlocker } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  Button,
  FormFieldActionTypes,
  SkeletonLoader,
  Text,
  TextLink,
  useAlert,
  useForm,
  useModalControl,
} from '@frontend/design-system';
import { ActiveOverridesModal } from '../../components/override/active-overrides-modal';
import { OverridePhoneTreeUsageBanner } from '../../components/override/override-phone-tree-usage-banner';
import { DeletePhoneTreeButton } from '../../components/phone-tree-quick-actions/delete-button';
import { PhoneTreeCard } from '../../components/phone-tree-quick-actions/phone-tree-card';
import { PromptCard } from '../../components/phone-tree-quick-actions/prompt-card';
import { DialOptionData } from '../../components/phone-tree-quick-actions/types';
import { usePhoneTreeMutations } from '../../components/phone-tree-quick-actions/use-phone-tree-mutations';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { trackingId } from '../../tracking';
import { CancelEditModal } from '../../utils/cancel-edit.modal';
import { logError } from '../../utils/loggers';
import { usePhoneTreeData } from './use-phone-tree-data';

export function PhoneTreeQuickActionsDetails() {
  const { t } = useTranslation('phone');
  const { navigate } = useSettingsNavigate();
  const alerts = useAlert();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const { params, search } = useSettingsMatch<{
    Params: { id: string };
    Search: { crid?: string; crn?: string; nid?: string };
  }>();

  const { modalProps: activeOverridesModalProps, openModal: openActiveOverridesModal } = useModalControl();

  // crid, crn and nid are set in the search params when the user is redirected from the call route details page.
  const [callRouteId, callRouteName, nodeId] = [search.crid, search.crn, search.nid];

  const {
    phoneTreeData,
    phoneTreeIsLoading,
    callObjectsLoading,
    transformedOptions: originalTransformedDialOptions,
  } = usePhoneTreeData({
    phoneTreeId: params.id,
    phoneTreeQueryOptions: {
      // Remove cache to prevent stale data from showing when navigating back
      staleTime: 0,
      cacheTime: 0,
      // Force refetch on mount to get fresh data
      refetchOnMount: 'always',
    },
  });

  const [state, setState] = useState(
    phoneTreeData ?? {
      phoneTreeId: '',
      tenantId: '',
      locationIds: [],
      name: '',
      promptMediaItemId: '',
      extensionDialing: false,
      dialOptions: [],
    }
  );
  const [transformedOptions, setTransformedOptions] = useState<DialOptionData[]>([]);

  useEffect(() => {
    if (originalTransformedDialOptions.length) {
      setTransformedOptions(originalTransformedDialOptions);
    }
  }, [originalTransformedDialOptions]);

  // Reset local state when phone tree data changes
  useEffect(() => {
    if (phoneTreeData) {
      setState(phoneTreeData);
      form.seedValues({ name: phoneTreeData.name });
      cancelModalProps.onClose();
    }
  }, [phoneTreeData]);

  const form = useForm({
    computeChangedValues: true,
    fields: {
      name: { type: 'text', value: phoneTreeData?.name },
    },
    fieldStateReducer: (formState, action) => {
      if (action.type === FormFieldActionTypes.Update) {
        // Update local state when name field changes
        setState((prevState) => ({
          ...prevState,
          name: action.payload.value,
        }));
      }

      return formState;
    },
  });

  const nameField = form.getFieldProps('name');

  const { updateDialOptions, update, invalidatePhoneTreeQueries } = usePhoneTreeMutations(
    settingsTenantLocation?.phoneTenantId ?? ''
  );

  const { modalProps: cancelModalProps, triggerProps: cancelTriggerProps } = useModalControl();

  const hasUnsavedChanges = useMemo(() => {
    const phoneTreeIdOutOfSync = phoneTreeData?.phoneTreeId !== state.phoneTreeId;

    if (phoneTreeIdOutOfSync) {
      // If there is a phoneTreeId mismatch, then this is a transitory state during phone tree switch.
      // We should not consider this as having unsaved changes and instead just update the ref to the latest state.
      return false;
    }

    return (
      JSON.stringify(phoneTreeData) !== JSON.stringify(state) ||
      JSON.stringify(originalTransformedDialOptions) !== JSON.stringify(transformedOptions)
    );
  }, [phoneTreeData, state, transformedOptions]);

  const { proceed } = useNavigationBlocker(
    {
      shouldBlockFn: () => {
        if (hasUnsavedChanges || cancelModalProps.show) {
          cancelTriggerProps.onClick();
          return true;
        }

        return false;
      },
      enableBeforeUnload: true,
    },
    [hasUnsavedChanges, cancelModalProps.show]
  );

  // Takes the transformed options and converts them back to the DialOptionType format to submit to the API
  const getDialOptionsForUpdate = (): DialOptionType[] => {
    const transformed = transformedOptions.map((option) => {
      const enrichedRoutes: DialOptionType['routes'] = [];

      for (const route of option.routes) {
        switch (route.type) {
          case RouteType.ROUTE_TYPE_CALL_GROUP: {
            enrichedRoutes.push({
              type: RouteType.ROUTE_TYPE_CALL_GROUP,
              payload: { ...route.originalPayload, callGroupId: route.id },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_CALL_QUEUE: {
            enrichedRoutes.push({
              type: route.type,
              payload: { ...route.originalPayload, callQueueId: route.id },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_IVR_MENU: {
            enrichedRoutes.push({
              type: route.type,
              payload: { ...route.originalPayload, subTreeId: route.id },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_FORWARDING_NUMBER: {
            enrichedRoutes.push({
              type: route.type,
              payload: { ...route.originalPayload, forwardingNumberId: route.id },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_CALL_ROUTE: {
            enrichedRoutes.push({
              type: route.type,
              payload: { ...route.originalPayload, callRouteId: route.id },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_DEVICE: {
            enrichedRoutes.push({
              type: route.type,
              payload: { ...route.originalPayload, deviceId: route.id },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_PLAY_MESSAGE: {
            enrichedRoutes.push({
              type: route.type,
              payload: { ...route.originalPayload, greetingId: route.id },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_PHONE_HOURS: {
            enrichedRoutes.push({
              type: route.type,
              payload: { ...route.originalPayload, phoneHoursId: route.id },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_VOICEMAIL: {
            enrichedRoutes.push({
              type: route.type,
              payload: { ...route.originalPayload, ...route.data },
            });

            break;
          }
          default:
            enrichedRoutes.push({ type: route.type });
        }
      }

      return {
        number: option.number,
        readOnly: false,
        routes: enrichedRoutes,
      };
    });

    return transformed;
  };

  const handleSave = async () => {
    const dialOptionsChanged = JSON.stringify(originalTransformedDialOptions) !== JSON.stringify(transformedOptions);
    const nonDialOptionStateChanged =
      phoneTreeData?.name !== state.name || phoneTreeData?.promptMediaItemId !== state.promptMediaItemId;

    const mutations = [];

    if (dialOptionsChanged) {
      mutations.push(
        updateDialOptions
          .mutateAsync({
            phoneTreeId: state.phoneTreeId,
            dialOptions: getDialOptionsForUpdate(),
          })
          .then(() => {
            // success alert will be shown after all mutations complete in the Promise.allSettled below
          })
          .catch(() => {
            alerts.error(t('System failed to update Phone Tree dial options. Please try again.'));
            throw new Error('Dial options update failed');
          })
      );
    }

    if (nonDialOptionStateChanged) {
      mutations.push(
        update
          .mutateAsync({
            phoneTreeId: state.phoneTreeId,
            tenantId: state.tenantId,
            locationIds: state.locationIds,
            name: nameField.value,
            promptMediaItemId: state.promptMediaItemId,
            extensionDialing: state.extensionDialing,
          })
          .then(() => {
            // success alert will be shown after all mutations complete in the Promise.allSettled below
          })
          .catch(() => {
            alerts.error(t('System failed to update Phone Tree. Please try again.'));
            throw new Error('Phone Tree update failed');
          })
      );
    }

    if (mutations.length > 0) {
      try {
        // Use Promise.allSettled to ensure all mutations complete regardless of failures
        const results = await Promise.allSettled(mutations);

        // Check if any mutations succeeded
        // const _hasSuccess = results.some((result) => result.status === 'fulfilled');
        const hasFailure = results.some((result) => result.status === 'rejected');

        if (!hasFailure) {
          // All mutations succeeded
          alerts.success(t('Phone Tree updated successfully.'));
          // Invalidate queries only once after mutations complete.
          // NOTE: it is important to call this only once here, not in individual mutation handlers above
          // to avoid making multiple queries calls which can lead to fetching stale data.
          invalidatePhoneTreeQueries(state.phoneTreeId);
        } else {
          // One or more mutations failed
          // Error alerts are already shown in individual catch blocks
          logError('PhoneTreeQuickActionsDetails:handleSave | One or more mutations failed during phone tree update', {
            data: { phoneTreeId: state.phoneTreeId, tenants: state.tenantId, locationIds: state.locationIds },
          });
        }
      } catch (error) {
        // This shouldn't happen with Promise.allSettled, but kept for safety
        console.error('Unexpected error in handleSave:', error);
        logError('PhoneTreeQuickActionsDetails:handleSave | Unexpected error during phone tree update', {
          data: { phoneTreeId: state.phoneTreeId, tenants: state.tenantId, locationIds: state.locationIds, error },
        });
      }
    }
  };

  const handleUpdateOptions = useCallback((oldOption: DialOptionData, updatedOption: DialOptionData) => {
    setTransformedOptions((prevDialOptions) => {
      const targetIndex = prevDialOptions.findIndex((o) => o.number === oldOption.number);
      const newOptions = [
        ...prevDialOptions.slice(0, targetIndex),
        updatedOption,
        ...prevDialOptions.slice(targetIndex + 1),
      ]
        .filter(Boolean)
        .sort((a, b) => a.number - b.number);
      return newOptions;
    });
  }, []);

  const handlePromptChange = useCallback((newPromptId: string) => {
    setState((prevState) => ({
      ...prevState,
      promptMediaItemId: newPromptId,
    }));
  }, []);

  const isLoading = updateDialOptions.isLoading || update.isLoading || phoneTreeIsLoading || callObjectsLoading;

  return (
    <PhoneSettingsPage
      renderContainer={false}
      hideLocationChip
      title={
        isLoading ? (
          <SkeletonLoader height={54} width={250} />
        ) : (
          <>{!!phoneTreeData && <EditableText alwaysShowIcon isTitle fieldProps={nameField} />}</>
        )
      }
      action={
        phoneTreeData && settingsTenantLocation?.phoneTenantId ? (
          <DeletePhoneTreeButton
            name={phoneTreeData.name}
            id={phoneTreeData.phoneTreeId}
            phoneTenantId={settingsTenantLocation.phoneTenantId}
            disabled={Boolean(phoneTreeData.callRouteData?.id)}
          />
        ) : null
      }
      subtitle={
        <span
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <Text>{t('Update Phone Tree prompts, forwarding numbers, Voicemail Boxes, and Voicemail Greetings.')}</Text>
          <span>
            {!!phoneTreeData && !!phoneTreeData.callRouteData?.id ? (
              <Text>
                <Trans t={t}>
                  Access advanced settings for this Phone Tree (<em>{phoneTreeData.name}</em>) within your{' '}
                  <TextLink
                    onClick={() =>
                      navigate({
                        to: '/phone/call-routes/:id',
                        params: { id: phoneTreeData.callRouteData?.id ?? '' },
                        context: { contextPrimitiveId: phoneTreeData.phoneTreeId },
                      })
                    }
                    trackingId={trackingId({
                      context: 'setting',
                      feature: 'phone-trees-quick-actions',
                      details: 'details-page-call-route-details-link',
                    })}
                  >
                    Call Route
                  </TextLink>{' '}
                  (<em>{phoneTreeData.callRouteData?.name ?? 'Unknown Name'}</em>) using the new Call Routes experience.
                </Trans>
              </Text>
            ) : (
              <Text as='span'>
                <Trans t={t}>
                  This Phone Tree is currently not in use in your call routes. Phone Trees are now created within your{' '}
                  <TextLink
                    onClick={() => navigate({ to: '/phone/call-routes' })}
                    target='_blank'
                    trackingId={trackingId({
                      context: 'setting',
                      feature: 'phone-trees-quick-actions',
                      details: 'details-page-call-routes-list-link',
                    })}
                  >
                    Call Routes.
                  </TextLink>
                </Trans>
              </Text>
            )}
          </span>
          <OverridePhoneTreeUsageBanner
            overrides={phoneTreeData?.overrides}
            trackingIdPrefix='ptqa-details-'
            onClickOverrideLink={() => {
              openActiveOverridesModal();
            }}
          />
        </span>
      }
      breadcrumbs={
        callRouteId
          ? [
              { label: t('Phone'), to: '/phone/main' },
              { label: t('Call Routes'), to: '/phone/call-routes' },
              {
                label: callRouteName ?? '',
                onClick: () =>
                  navigate({
                    to: '/phone/call-routes/:id',
                    params: { id: callRouteId },
                    context: { contextNodeId: nodeId },
                  }),
              },
              {
                label: phoneTreeData?.name ?? '',
                to: '/phone/phone-tree-quick-actions/:id',
                params: { id: params.id },
              },
            ]
          : [
              { label: t('Phone'), to: '/phone/main' },
              { label: t('Phone Tree Quick Actions'), to: '/phone/phone-tree-quick-actions' },
              {
                label: phoneTreeData?.name ?? '',
                to: '/phone/phone-tree-quick-actions/:id',
                params: { id: params.id },
              },
            ]
      }
    >
      {/* Only Show the skeleton loader while fetching the phone tree data and not when updating */}
      {phoneTreeIsLoading ? (
        <SkeletonLoader count={2} height={300} distance={10} />
      ) : (
        <>
          {!!phoneTreeData && !!settingsTenantLocation ? (
            <>
              <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                <PromptCard
                  onPromptChange={handlePromptChange}
                  tenantId={settingsTenantLocation.phoneTenantId ?? ''}
                  greetingId={state.promptMediaItemId}
                  disabled={isLoading}
                />
                <PhoneTreeCard
                  options={transformedOptions}
                  onOptionChange={handleUpdateOptions}
                  disableEditing={isLoading}
                />
                <div>
                  <Button
                    onClick={handleSave}
                    disabled={isLoading || !hasUnsavedChanges}
                    css={{ width: 'min-content' }}
                    trackingId={trackingId({
                      context: 'setting',
                      feature: 'phone-trees-quick-actions',
                      details: 'save',
                    })}
                  >
                    {t('Save Changes')}
                  </Button>
                </div>
              </div>

              <CancelEditModal
                disabled={false}
                modalProps={cancelModalProps}
                onQuitClick={proceed}
                onSaveAndQuitClick={() => handleSave().then(() => proceed())}
              />
            </>
          ) : (
            <NoPhoneTreeDataMessage />
          )}
        </>
      )}

      <ActiveOverridesModal
        modalProps={activeOverridesModalProps}
        title={t('Call Routes with Active Overrides')}
        overrides={phoneTreeData?.overrides ?? []}
      />
    </PhoneSettingsPage>
  );
}

const NoPhoneTreeDataMessage = () => {
  const { t } = useTranslation('phone');
  const { navigate } = useSettingsNavigate();

  return (
    <Text>
      <Trans t={t}>
        No phone tree data found. Back to{' '}
        <TextLink
          onClick={() =>
            navigate({
              to: '/phone/phone-tree-quick-actions',
            })
          }
          trackingId={trackingId({
            context: 'setting',
            feature: 'phone-trees-quick-actions',
            details: 'details-page-no-data-phone-trees-link',
          })}
        >
          Phone Trees
        </TextLink>
        .
      </Trans>
    </Text>
  );
};
